import type { BuiltNavNode, NavTarget } from './types'

export interface NavigationSearchDestination {
  id: string
  label: string
  /** Route to push. Absent for an action leaf, which acts in place of navigating. */
  target?: NavTarget
  /** An action leaf's own handler (ORP-27467 Orphex MCP) — run inside the selecting gesture. */
  action?: () => unknown
  importance: number
  searchValue: string
}

type LabelResolver = (labelKey: string) => string

function metadata(
  node: BuiltNavNode,
): Pick<NavigationSearchDestination, 'importance' | 'searchValue'> {
  const importance = node.meta?.['importance']
  const searchValue = node.meta?.['searchValue']
  return {
    importance: typeof importance === 'number' && Number.isFinite(importance) ? importance : -1,
    searchValue: typeof searchValue === 'string' ? searchValue : '',
  }
}

function ownDestination(
  node: BuiltNavNode,
  labelOf: LabelResolver,
): NavigationSearchDestination | null {
  if (!node.labelKey && !node.label) return null
  const label = node.label ?? labelOf(node.labelKey ?? '')
  if (node.to) {
    return {
      id: `${node.to.name}:${JSON.stringify(node.to.params ?? {})}`,
      label,
      target: node.to,
      ...metadata(node),
    }
  }
  if (typeof node.action === 'function') {
    return { id: `action:${node.navKey ?? label}`, label, action: node.action, ...metadata(node) }
  }
  return null
}

/**
 * Flatten only permitted, routed — or action — built nodes; visibility was already enforced by
 * useNavigation.
 */
export function flattenNavigationDestinations(
  nodes: readonly BuiltNavNode[],
  labelOf: LabelResolver,
): NavigationSearchDestination[] {
  return nodes.flatMap((node) => {
    const own = ownDestination(node, labelOf)
    return [...(own ? [own] : []), ...flattenNavigationDestinations(node.children ?? [], labelOf)]
  })
}

/** v3 search semantics: all words must match label or metadata; label relevance wins, then importance. */
export function searchNavigationDestinations(
  destinations: readonly NavigationSearchDestination[],
  query: string,
  limit = 8,
): NavigationSearchDestination[] {
  const needle = query.trim().toLocaleLowerCase()
  if (!needle) return []
  const words = needle.split(/\s+/u).filter(Boolean)

  return destinations
    .map((destination) => {
      const label = destination.label.toLocaleLowerCase()
      const haystack = `${label} ${destination.searchValue.toLocaleLowerCase()}`
      const relevance =
        label === needle ? 3 : label.startsWith(needle) ? 2 : label.includes(needle) ? 1 : 0
      return { destination, haystack, relevance }
    })
    .filter(({ haystack }) => words.every((word) => haystack.includes(word)))
    .sort(
      (left, right) =>
        right.relevance - left.relevance ||
        right.destination.importance - left.destination.importance ||
        left.destination.label.localeCompare(right.destination.label),
    )
    .slice(0, limit)
    .map(({ destination }) => destination)
}
